import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@material-ui/core';
import { trustpilotReview } from '../constants';
import { detectAlpha2Country } from '../../../utils/country';

const SCRIPT_SRC =
  'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
const TEMPLATE_ID = '56278e9abfbbba0bdcd568bc'; // Review Collector

// gproxy normalises the WMS/OMS dispatch statuses of the briefing
// (CREATED / STOCK_OK / SHIPMENT_GO / PLANNED / PACKED / SHIPPED / SHIPPED DELIVERED)
// into these four. Anything else (RETURNED*, BLOCKED, CANCELLED, OUT_OF_STOCK, ...)
// means the order had an incident.
const OK_DISPATCH_STATUS = ['CREATED', 'PACKAGING', 'SHIPPED', 'DELIVERED'];

export const isTrustpilotEligible = (order, shipments) => {
  if (!order || order.status !== 'RUNNING') {
    return false;
  }
  const dispatches = [
    ...((shipments && shipments.closed) || []),
    ...((shipments && shipments.open) || []),
  ].sort((a, b) => a.sequence - b.sequence);

  // max dispatch number 2
  if (dispatches.length < 1 || dispatches.length > 2) {
    return false;
  }
  if (
    !dispatches.every((d) =>
      OK_DISPATCH_STATUS.includes(d && d.shipment && d.shipment.status),
    )
  ) {
    return false;
  }
  // with a single dispatch the briefing excludes CREATED: the first package must
  // be at least in preparation, otherwise there is nothing to review yet.
  return dispatches[0].shipment.status !== 'CREATED';
};

const loadScript = () =>
  new Promise((resolve) => {
    if (window.Trustpilot) {
      return resolve();
    }
    let script = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', resolve);
  });

const TrustpilotReview = ({ className }) => {
  const { t } = useTranslation(['components']);
  const widget = useRef(null);
  const config =
    process.env.REACT_APP_TRUSTPILOT_ENABLED === 'false'
      ? null
      : (trustpilotReview[detectAlpha2Country()] || {})[
          process.env.REACT_APP_BRAND
        ] || null;

  const trackClick = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'trustpilot_click',
      location: 'detalles_suscripcion',
      page_name: 'mi_cuenta_suscripciones',
      page_type: 'user_account',
    });
  };

  // the widget renders in an iframe: a click inside it never reaches us, but it
  // does steal focus, so a window blur with the iframe focused is the click.
  useEffect(() => {
    const onBlur = () => {
      if (
        widget.current &&
        document.activeElement &&
        widget.current.contains(document.activeElement)
      ) {
        trackClick();
      }
    };
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, []);

  useEffect(() => {
    if (config) {
      loadScript().then(() => {
        if (window.Trustpilot && widget.current) {
          window.Trustpilot.loadFromElement(widget.current, true);
        }
      });
    }
  }, [config]);

  if (!config) {
    return null;
  }

  return (
    <Box
      className={className}
      p={2}
      style={{ borderBottom: '1px solid #f2f2f2' }}>
      <Box mt={1} onClick={trackClick}>
        <div
          ref={widget}
          className="trustpilot-widget"
          data-locale={config.locale}
          data-template-id={TEMPLATE_ID}
          data-businessunit-id={config.businessunitId}
          data-style-height="52px"
          data-style-width="100%"
          data-token={config.token}
          data-text-color="#3b4154"
          data-border-color={config.borderColor}>
          <a href={config.url} target="_blank" rel="noopener noreferrer">
            Trustpilot
          </a>
        </div>
      </Box>
    </Box>
  );
};

export default TrustpilotReview;
